/* ========== STANDARD CHART PARSER ==========
 * Parses an OOXML <c:chart> document into a renderer-friendly object.
 *
 * Flow:
 *   1. Read the title (rich/strCache/t fallbacks)
 *   2. Detect every chart-type element in plotArea (chartTypeDetector)
 *   3. Pick a primary chart type — special-cases barDir → col, ofPieType
 *      → pieOfPie/barOfPie
 *   4. Parse every <c:ser> across all groups (seriesParser)
 *   5. Read shared categories (seriesParser)
 *   6. Pull ofPie geometry through from the primary group
 *
 * Extended charts (Treemap, Sunburst, etc.) go through extendedChartParser
 * instead, since they use a completely different XML schema.
 * ============================================ */

import { qLocal, qLocalAll, qDirectChild } from "../xmlUtils";
import { detectChartGroups } from "./chartTypeDetector";
import { parseSeries, readCategories, readCategoryLevels } from "./seriesParser";

/**
 * Extract the chart title. Excel writes this in several different ways
 * depending on version; we try them in order until one returns text.
 */
function readChartTitle(chartDoc) {
  const chartElem = qLocal(chartDoc, "chart");
  const titleNode = chartElem ? qDirectChild(chartElem, "title") : qLocal(chartDoc, "title");
  if (!titleNode) return "Chart";

  // 1. rich text inside tx
  const txNode = qLocal(titleNode, "tx");
  if (txNode) {
    const richNode = qLocal(txNode, "rich");
    if (richNode) {
      const tNodes = qLocalAll(richNode, "t");
      if (tNodes.length > 0) {
        const text = tNodes.map(t => t.textContent).join("").trim();
        if (text) return text;
      }
    }
    // 2. cached string ref
    const strCache = qLocal(txNode, "strCache");
    if (strCache) {
      const vNode = qLocal(strCache, "v");
      if (vNode && vNode.textContent.trim()) return vNode.textContent.trim();
    }
  }
  // 3. fallback to any <a:t> under the title
  const fallbackTexts = qLocalAll(titleNode, "t");
  if (fallbackTexts.length > 0) {
    const text = fallbackTexts.map(t => t.textContent).join("").trim();
    if (text) return text;
  }
  return "Chart";
}

/**
 * Decide the final chartType string the renderer's switch will see.
 *   - barChart + barDir="bar" → "bar" (horizontal)
 *   - barChart + barDir="col" → "col" (vertical)
 *   - ofPieChart + ofPieType="bar" → "barOfPie"
 *   - ofPieChart + ofPieType="pie" → "pieOfPie"
 *   - everything else passes its renderType through unchanged
 */
function resolvePrimaryChartType(primary) {
  if (primary.renderType === "bar") {
    return primary.barDir === "col" ? "col" : "bar";
  }
  if (primary.renderType === "ofPie") {
    return primary.ofPieType === "bar" ? "barOfPie" : "pieOfPie";
  }
  return primary.renderType;
}

export function parseStandardChart(chartDoc) {
  const result = {
    title:      readChartTitle(chartDoc),
    chartType:  "bar",
    isCombo:    false,
    series:     [],
    categories: [],
  };

  const plotArea = qLocal(chartDoc, "plotArea");
  if (!plotArea) return result;

  const chartGroups = detectChartGroups(plotArea);
  if (chartGroups.length === 0) return result;

  // Primary group decides chartType / grouping / 3D / markers.
  const primary = chartGroups[0];
  result.chartType = resolvePrimaryChartType(primary);
  result.grouping  = primary.grouping;
  result.is3D      = primary.is3D;
  result.hasMarker = primary.hasMarker;
  if (primary.ofPieGeom) result.ofPieGeom = primary.ofPieGeom;
  if (primary.radarStyle) result.radarStyle = primary.radarStyle;
  if (primary.dataLabels) result.dataLabels = primary.dataLabels;

  // Combo = chart has multiple distinct render types in its plotArea.
  const uniqueTypes = Array.from(new Set(chartGroups.map(g => g.renderType)));
  result.isCombo = uniqueTypes.length > 1;

  // Walk every series across every chart group. Excel may write <c:ser>
  // elements out of display order — <c:order val="N"/> is authoritative.
  // Without sorting, stacked charts stack segments in the wrong order and
  // clustered charts show series swapped ("wrong data" symptoms).
  const allSerNodes = [];
  const collected = [];
  for (const group of chartGroups) {
    const serNodes = qLocalAll(group.element, "ser");
    serNodes.forEach(ser => {
      const seriesObj = parseSeries(ser, group.renderType, group.barDir);
      if (seriesObj) {
        const orderNode = qLocal(ser, "order");
        const orderVal = orderNode ? parseInt(orderNode.getAttribute("val") || "0", 10) : collected.length;
        collected.push({ order: isNaN(orderVal) ? collected.length : orderVal, seriesObj });
      }
      allSerNodes.push(ser);
    });
  }
  collected.sort((a, b) => a.order - b.order);
  collected.forEach(c => result.series.push(c.seriesObj));

  result.categories = readCategories(allSerNodes);
  result.catLevels = readCategoryLevels(allSerNodes);
  return result;
}
